import {
  useCallback, useRef, useState, type RefObject,
} from 'react';
import useIntersectionObserver from 'src/common/hooks/useIntersectionObserver';

type UseDynamicHeightProps = {
  ref: RefObject<HTMLDivElement>;
  isOpen: boolean;
  isDesktop: boolean;
};

/**
 * This hook ensures the popover height fills available space below, without exceeding viewport.
 *
 * Why: Search Popovers (desktop) are not positioned relative to a 100vh container;
 * therefore a `max-height: 80vh` can still exceed the viewport height.
 * Especially when positioned relative to elements in the middle of the viewport.
 *
 * The return value is used to set the height of popover via a css variable.
 *
 * Other edge behavior handled:
 * - if user scrolls the page down, the height will increase to reveal overflow items
 * - if user resizes window, the height will increase to fill available window height
 * - if user scrolls a popover past overflow, the scroll will not transfer to page behind
 * */
const useDynamicHeight = ({
  ref, isOpen, isDesktop,
}: UseDynamicHeightProps): number | undefined => {
  const onScreen = useRef(false);
  const [popoverHeight, setPopoverHeight] = useState<number>();

  const resizePopover = useCallback(() => {
    if (!isOpen || !ref.current || !isDesktop) return;
    const posY = ref.current.getBoundingClientRect().y;
    let distanceFromBottom = window.innerHeight - posY;
    distanceFromBottom = Math.max(distanceFromBottom, 200); // min-height clamp
    const paddingFromEdge = 20;
    setPopoverHeight(distanceFromBottom - paddingFromEdge);
  }, [isDesktop, isOpen, ref]);

  useIntersectionObserver<HTMLDivElement>({
    threshold: 0,
    ref,
    callback: ([entry]) => {
      if (!isDesktop) { return; }
      if (entry.isIntersecting === onScreen.current) { return; }

      onScreen.current = entry.isIntersecting;
      resizePopover();

      if (entry.isIntersecting) {
        window.addEventListener('resize', resizePopover);
        window.addEventListener('scroll', resizePopover);
      } else {
        window.removeEventListener('resize', resizePopover);
        window.removeEventListener('scroll', resizePopover);
      }
    },
  });

  return popoverHeight;
};

export default useDynamicHeight;
